import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectActiveWinery } from "../context/selectors";
import { setUserRbac, setUserRbacFailed } from "../context/user/userSlice";
import { getUserRbac } from "../services/utils";
import useAxiosPrivate from "./useAxiosPrivate";
import useAuth from "./useAuth";
import { ROLES } from "../constants/base";

// Loads GET /users/me/rbac for the active winery into the store: explicit permissions (menu),
// effective permissions (what the server allows), grants_all and role codes.
const useRbacSync = () => {
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const activeWinery = useSelector(selectActiveWinery);
  const { auth } = useAuth();

  useEffect(() => {
    // L'admin non ha cantina ne' ruoli RBAC di cantina (il BE lo lascia passare
    // sempre). Con una cantina ormai stantia (uscita da un'impersonazione) la
    // richiesta avrebbe 403 con la cantina del target o 428 se lo svuotamento
    // arriva prima che axios calcoli gli header.
    if (Number(auth?.role) === ROLES.ADMIN) return;

    if (!activeWinery?.id || !auth?.accessToken) return;

    const wineryId = activeWinery.id;
    // An answer for a winery or a token that is no longer current is dropped (it would mark the
    // store as loaded for a winery the user already left).
    let current = true;

    getUserRbac(axiosPrivate)
      .then((body) => {
        if (!current) return;
        // getUserRbac swallows its errors and answers {}: that is a failure, not "no permissions".
        const empty = !body || typeof body !== "object" || Object.keys(body).length === 0;
        dispatch(empty ? setUserRbacFailed(wineryId) : setUserRbac(body, wineryId));
      })
      .catch((err) => {
        // Nothing is granted on failure, but the loading ends (setUserRbacFailed): a broken API shows
        // /unauthorized on the guarded routes instead of a loader for ever. The permissions already
        // loaded for the same winery stay (a failed refresh).
        console.error('useRbacSync: Error fetching RBAC from API:', err.message);
        if (current) dispatch(setUserRbacFailed(wineryId));
      });

    return () => {
      current = false;
    };
  }, [activeWinery?.id, auth?.accessToken, auth?.role, dispatch]);
};

export default useRbacSync;
